import { useEffect, useRef, useState, useCallback } from "react";
import { EventLayout } from "@/components/EventLayout";
import { Gate } from "@/components/FeatureGate";
import { useEvent } from "@/context/EventContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Copy, Circle, RectangleHorizontal, Printer, Image as ImageIcon, Minus, Search, Users, AlertTriangle } from "lucide-react";

const DRAG_THRESHOLD = 4;

export default function Seating() {
  const { eapi, eventId } = useEvent();
  const [tables, setTables] = useState([]);
  const [guests, setGuests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [bpUrl, setBpUrl] = useState(null);
  const [bpMeta, setBpMeta] = useState(null);
  const [opacity, setOpacity] = useState(0.85);
  const [search, setSearch] = useState("");
  const canvasRef = useRef(null);
  const dragState = useRef(null);
  const fileRef = useRef(null);

  const load = async () => {
    const [t, g] = await Promise.all([eapi.get("/tables"), eapi.get("/guests")]);
    setTables(t.data); setGuests(g.data);
  };
  const loadBlueprint = async () => {
    try {
      const m = await eapi.get("/blueprint");
      if (m.data.blueprint) {
        const b = await eapi.get("/blueprint/file", { responseType: "blob" });
        setBpUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(b.data); });
        setBpMeta(m.data.blueprint);
      } else { setBpUrl(null); setBpMeta(null); }
    } catch { /* no blueprint */ }
  };
  useEffect(() => { load(); loadBlueprint(); }, []);

  const seatable = guests.filter((g) => (g.approval_status || "approved") !== "rejected");
  const unassigned = seatable.filter((g) => !g.table_id);
  const seatedCount = seatable.filter((g) => g.table_id).length;
  const seatedOf = (tid) => seatable.filter((g) => g.table_id === tid);
  const flaggedOf = (list) => list.filter((g) => (g.rsvp_status || "pending") !== "attending");
  const rsvpTally = (list) => ({
    attending: list.filter((g) => g.rsvp_status === "attending").length,
    pending: list.filter((g) => (g.rsvp_status || "pending") === "pending").length,
    declined: list.filter((g) => g.rsvp_status === "declined").length,
  });
  const dotColor = (g) => (g.rsvp_status === "attending" ? "#8C9A86" : g.rsvp_status === "declined" ? "#C67A7A" : "#C6A15B");
  const selected = tables.find((t) => t.id === selectedId) || null;

  const autoNumber = async () => {
    await eapi.post("/tables/auto-number");
    toast.success("Numbered any empty or duplicate tables");
    load();
  };

  const addTable = async () => {
    const res = await eapi.post("/tables", { name: `Table ${tables.length + 1}`, shape: tables.length % 2 ? "rectangle" : "round", capacity: 8 });
    await load(); setSelectedId(res.data.id);
  };
  const duplicate = async (t) => {
    const res = await eapi.post("/tables", { name: `${t.name} copy`, shape: t.shape, capacity: t.capacity });
    const nt = res.data;
    await eapi.put(`/tables/${nt.id}/layout`, { x: (t.x || 0) + 28, y: (t.y || 0) + 28, w: t.w, h: t.h, shape: t.shape, capacity: t.capacity });
    await load(); setSelectedId(nt.id);
  };
  const removeTable = async (id) => { await eapi.delete(`/tables/${id}`); if (selectedId === id) setSelectedId(null); toast.success("Table removed"); load(); };
  const assign = async (guestId, tableId) => {
    try { await eapi.post(`/guests/${guestId}/assign`, { table_id: tableId }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleShape = async (t) => {
    const shape = t.shape === "round" ? "rectangle" : "round";
    setTables((ts) => ts.map((x) => (x.id === t.id ? { ...x, shape } : x)));
    await eapi.put(`/tables/${t.id}/layout`, { shape });
  };
  const setCapacity = async (t, cap) => {
    cap = Math.max(1, Math.min(cap, 40));
    setTables((ts) => ts.map((x) => (x.id === t.id ? { ...x, capacity: cap } : x)));
    await eapi.put(`/tables/${t.id}/layout`, { capacity: cap });
  };
  const setNumber = async (t, num) => {
    setTables((ts) => ts.map((x) => (x.id === t.id ? { ...x, number: num } : x)));
    await eapi.put(`/tables/${t.id}/layout`, { number: num });
    load();
  };
  const rename = async (t, name) => {
    if (!name.trim() || name === t.name) return;
    await eapi.put(`/tables/${t.id}`, { name: name.trim(), shape: t.shape, capacity: t.capacity }); load();
  };

  // pointer move / resize with click-to-select detection
  const onMove = useCallback((e) => {
    const ds = dragState.current; if (!ds) return;
    const rect = canvasRef.current.getBoundingClientRect();
    if (Math.abs(e.clientX - ds.startX) > DRAG_THRESHOLD || Math.abs(e.clientY - ds.startY) > DRAG_THRESHOLD) ds.moved = true;
    if (ds.mode === "move") {
      const w = ds.w, h = ds.h;
      let x = Math.max(0, Math.min(e.clientX - rect.left - ds.offsetX, rect.width - w));
      let y = Math.max(0, Math.min(e.clientY - rect.top - ds.offsetY, rect.height - h));
      setTables((ts) => ts.map((t) => (t.id === ds.id ? { ...t, x, y } : t)));
    } else {
      let w = Math.max(20, Math.min(ds.startW + (e.clientX - ds.startX), 480));
      let h = Math.max(20, Math.min(ds.startH + (e.clientY - ds.startY), 420));
      setTables((ts) => ts.map((t) => (t.id === ds.id ? { ...t, w, h } : t)));
    }
  }, []);
  const onUp = useCallback(() => {
    const ds = dragState.current; dragState.current = null;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    if (!ds) return;
    if (!ds.moved && ds.mode === "move") { setSelectedId(ds.id); return; }
    const rect = canvasRef.current?.getBoundingClientRect();
    setTables((cur) => {
      const t = cur.find((x) => x.id === ds.id);
      if (t) eapi.put(`/tables/${t.id}/layout`, { x: Math.round(t.x || 0), y: Math.round(t.y || 0), w: Math.round(t.w), h: Math.round(t.h), cw: Math.round(rect?.width || 0), ch: Math.round(rect?.height || 0) });
      return cur;
    });
  }, [onMove]);
  const startDrag = (e, t, mode) => {
    e.preventDefault(); e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    const w = t.w || 150, h = t.h || 150;
    dragState.current = { mode, id: t.id, moved: false, w, h,
      offsetX: e.clientX - rect.left - (t.x || 0), offsetY: e.clientY - rect.top - (t.y || 0),
      startX: e.clientX, startY: e.clientY, startW: w, startH: h };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const onCanvasPointerDown = (e) => { if (!e.target.closest("[data-table]")) setSelectedId(null); };

  const uploadBlueprint = async (files) => {
    const file = files?.[0]; if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("This image is too large. The maximum size is 10 MB."); return; }
    const fd = new FormData(); fd.append("file", file);
    try { await eapi.post("/blueprint", fd); toast.success("Floor plan uploaded"); loadBlueprint(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeBlueprint = async () => { await eapi.delete("/blueprint"); if (bpUrl) URL.revokeObjectURL(bpUrl); setBpUrl(null); setBpMeta(null); toast.success("Floor plan removed"); };

  const searched = unassigned.filter((g) => `${g.first_name} ${g.last_name}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <EventLayout>
      <Gate featureKey="seating">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 lg:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="overline">Seating planner</p>
            <h1 className="font-serif text-4xl tracking-tight">Floor plan</h1>
            <p className="text-sm text-stone-500 mt-1">{seatedCount} seated · {unassigned.length} to place · drag tables anywhere, drag the corner to resize, click a table to add guests</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-stone-400" data-testid="seating-legend">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: "#8C9A86" }} /> Attending</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: "#C6A15B" }} /> Pending</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: "#C67A7A" }} /> Declined</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" hidden accept=".jpg,.jpeg,.png" data-testid="blueprint-input"
              onChange={(e) => { uploadBlueprint(e.target.files); e.target.value = ""; }} />
            <button onClick={() => fileRef.current?.click()} data-testid="upload-blueprint-btn"
              className="flex items-center gap-2 rounded-full border border-[#E8E6E1] text-stone-600 px-5 py-2.5 text-sm hover:bg-stone-50 transition-colors">
              <ImageIcon size={15} /> {bpMeta ? "Replace floor plan" : "Upload floor plan"}
            </button>
            <button onClick={() => (window.location.href = `/e/${eventId}/print/seating`)} data-testid="print-seating-btn"
              className="flex items-center gap-2 rounded-full border border-[#E8E6E1] text-stone-600 px-5 py-2.5 text-sm hover:bg-stone-50 transition-colors">
              <Printer size={15} /> Print chart
            </button>
            <button onClick={autoNumber} data-testid="auto-number-btn" title="Fills in missing or duplicate table numbers without changing numbers you've set"
              className="flex items-center gap-2 rounded-full border border-[#8C9A86] text-[#5f6b59] px-4 py-2.5 text-sm hover:bg-[#8C9A86]/10 transition-colors mr-2">Auto-number tables</button>
            <button onClick={addTable} data-testid="add-table-btn"
              className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-5 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors">
              <Plus size={16} /> Add table
            </button>
          </div>
        </div>

        {bpMeta && (
          <div className="flex items-center gap-3 mb-3 text-xs text-stone-500">
            <span className="flex items-center gap-1.5"><ImageIcon size={13} className="text-[#8C9A86]" /> {bpMeta.filename}</span>
            <label className="flex items-center gap-2">Opacity
              <input type="range" min="0.15" max="1" step="0.05" value={opacity} data-testid="blueprint-opacity"
                onChange={(e) => setOpacity(parseFloat(e.target.value))} className="accent-[#8C9A86]" />
            </label>
            <button onClick={removeBlueprint} data-testid="remove-blueprint-btn" className="text-[#C67A7A] hover:underline">Remove</button>
          </div>
        )}

        <div ref={canvasRef} data-testid="floor-plan" onPointerDown={onCanvasPointerDown}
          className="relative rounded-xl border border-[#E8E6E1] bg-[#FBFAF6] overflow-hidden touch-none"
          style={{ minHeight: 660, ...(bpUrl ? {} : { backgroundImage: "radial-gradient(#E3E0D8 1px, transparent 1px)", backgroundSize: "24px 24px" }) }}>
          {bpUrl && <img src={bpUrl} alt="Floor plan" draggable={false} className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none" style={{ opacity }} />}
          {tables.length === 0 && (
            <div className="absolute inset-0 grid place-items-center text-sm text-stone-400 pointer-events-none">Add a table to begin arranging your floor plan.</div>
          )}
          {tables.map((t) => {
            const seated = seatedOf(t.id);
            const full = seated.length >= t.capacity;
            const flagged = flaggedOf(seated).length;
            const tally = rsvpTally(seated);
            const isSel = selectedId === t.id;
            const round = t.shape === "round";
            const w = t.w || 150, h = t.h || 150;
            return (
              <div key={t.id} data-table data-testid={`table-card-${t.id}`}
                onPointerDown={(e) => startDrag(e, t, "move")}
                style={{ left: t.x || 0, top: t.y || 0, width: w, height: h }}
                className="absolute select-none cursor-grab active:cursor-grabbing group">
                {isSel && (
                  <div className="absolute -top-9 left-0 flex items-center gap-1 bg-white border border-[#E8E6E1] rounded-full shadow px-1 py-0.5 z-10">
                    <button onPointerDown={(e) => e.stopPropagation()} onClick={() => toggleShape(t)} data-testid={`shape-table-${t.id}`} title="Toggle shape"
                      className="p-1.5 text-stone-500 hover:text-[#8C9A86]">{round ? <Circle size={14} /> : <RectangleHorizontal size={14} />}</button>
                    <button onPointerDown={(e) => e.stopPropagation()} onClick={() => duplicate(t)} data-testid={`duplicate-table-${t.id}`} title="Duplicate"
                      className="p-1.5 text-stone-500 hover:text-[#8C9A86]"><Copy size={14} /></button>
                    <button onPointerDown={(e) => e.stopPropagation()} onClick={() => removeTable(t.id)} data-testid={`remove-table-${t.id}`} title="Delete"
                      className="p-1.5 text-stone-500 hover:text-[#C67A7A]"><Trash2 size={14} /></button>
                  </div>
                )}
                <div className={`relative w-full h-full flex items-center justify-center bg-white/95 border-2 shadow-[0_8px_32px_rgba(0,0,0,0.06)] transition-colors ${round ? "rounded-full" : "rounded-xl"} ${isSel ? "border-[#8C9A86] ring-4 ring-[#8C9A86]/20" : "border-[#D8D4CC]"}`}
                  style={round ? { borderRadius: "50%" } : {}}>
                  {flagged > 0 && (
                    <div data-testid={`table-warning-${t.id}`} title={`${flagged} guest(s) not confirmed`}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#C6A15B] text-white grid place-items-center shadow z-10">
                      <AlertTriangle size={13} />
                    </div>
                  )}
                  <span className={`font-serif leading-none ${full ? "text-[#C67A7A]" : "text-stone-700"}`}
                    style={{ fontSize: Math.max(11, Math.min(Math.min(w, h) * 0.45, 40)) }}>{t.number ?? "–"}</span>
                  <div data-testid={`table-tooltip-${t.id}`}
                    className={`pointer-events-none absolute ${(t.x || 0) < 190 ? "left-full ml-3" : "right-full mr-3"} top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[#2C2A28] text-white text-xs px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-20 shadow-lg`}>
                    <span className="block">{t.name}: {seated.length}/{t.capacity} seated</span>
                    <span className="block text-white/70">{tally.attending} attending · {tally.pending} pending · {tally.declined} declined</span>
                    {flagged > 0 && <span className="block text-[#F0C674] mt-0.5">Warning: {[tally.pending ? `${tally.pending} pending RSVP` : "", tally.declined ? `${tally.declined} declined` : ""].filter(Boolean).join(", ")}</span>}
                  </div>
                </div>
                {isSel && (
                  <div onPointerDown={(e) => startDrag(e, t, "resize")} data-testid={`resize-table-${t.id}`}
                    className="absolute -bottom-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border-2 border-[#8C9A86] cursor-nwse-resize z-10" title="Drag to resize" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Slide-out guest panel */}
      <div data-testid="seating-panel"
        className={`fixed top-0 right-0 h-full w-[340px] max-w-[88vw] bg-white border-l border-[#E8E6E1] shadow-[-12px_0_40px_rgba(0,0,0,0.08)] z-40 transition-transform duration-300 ease-out ${selected ? "translate-x-0" : "translate-x-full"}`}>
        {selected && (
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#F0EEE9]">
              <div className="min-w-0">
                <p className="overline">Table</p>
                <input defaultValue={selected.name} key={selected.id} onBlur={(e) => rename(selected, e.target.value)} data-testid="panel-rename"
                  className="font-serif text-2xl tracking-tight w-full outline-none bg-transparent focus:border-b focus:border-[#8C9A86]" />
              </div>
              <button onClick={() => setSelectedId(null)} data-testid="panel-close" className="p-2 text-stone-400 hover:text-stone-700"><X size={20} /></button>
            </div>

            <div className="flex items-center gap-3 px-5 py-3 border-b border-[#F0EEE9]">
              <span className="text-xs text-stone-500">Table number</span>
              <select value={selected.number || ""} onChange={(e) => setNumber(selected, parseInt(e.target.value, 10))} data-testid="panel-number"
                className="rounded-md border border-[#E8E6E1] bg-white px-2.5 py-1.5 text-sm outline-none focus:border-[#8C9A86]">
                {Array.from({ length: Math.max(tables.length, selected.number || 1) }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>Table {n}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3 px-5 py-3 border-b border-[#F0EEE9]">
              <span className="text-xs text-stone-500">Capacity</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setCapacity(selected, selected.capacity - 1)} data-testid="panel-capacity-dec" className="p-1.5 rounded-md border border-[#E8E6E1] hover:bg-stone-50"><Minus size={13} /></button>
                <span className="w-8 text-center text-sm font-medium">{selected.capacity}</span>
                <button onClick={() => setCapacity(selected, selected.capacity + 1)} data-testid="panel-capacity-inc" className="p-1.5 rounded-md border border-[#E8E6E1] hover:bg-stone-50"><Plus size={13} /></button>
              </div>
              <button onClick={() => toggleShape(selected)} className="ml-auto flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#8C9A86]">
                {selected.shape === "round" ? <Circle size={13} /> : <RectangleHorizontal size={13} />} {selected.shape}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <p className="overline mb-2">Seated ({seatedOf(selected.id).length}/{selected.capacity})</p>
              {flaggedOf(seatedOf(selected.id)).length > 0 && (
                <div data-testid="panel-table-warning" className="flex items-start gap-2 mb-3 rounded-md bg-[#C6A15B]/12 text-[#8a6d2f] px-3 py-2 text-xs">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  <span>Some guests here haven't confirmed: {rsvpTally(seatedOf(selected.id)).pending} pending, {rsvpTally(seatedOf(selected.id)).declined} declined.</span>
                </div>
              )}
              <div className="space-y-1.5 mb-5">
                {seatedOf(selected.id).length === 0 && <p className="text-xs text-stone-400">No one seated here yet.</p>}
                {seatedOf(selected.id).map((g) => (
                  <div key={g.id} data-testid={`panel-seated-${g.id}`} className="flex items-center justify-between bg-[#8C9A86]/10 rounded-md px-3 py-2">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dotColor(g) }} title={g.rsvp_status || "pending"} />
                      <span className="text-sm text-stone-700 truncate">{g.first_name} {g.last_name}</span>
                    </span>
                    <button onClick={() => assign(g.id, null)} data-testid={`panel-unseat-${g.id}`} className="text-stone-400 hover:text-[#C67A7A]"><X size={14} /></button>
                  </div>
                ))}
              </div>

              <p className="overline mb-2">Add a guest</p>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-300" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search unseated guests" data-testid="panel-guest-search"
                  className="w-full rounded-md border border-[#E8E6E1] pl-9 pr-3 py-2 text-sm outline-none focus:border-[#8C9A86]" />
              </div>
              <div className="space-y-1.5">
                {searched.length === 0 && <p className="text-xs text-stone-400">{unassigned.length === 0 ? "Everyone is seated." : "No matching guests."}</p>}
                {searched.map((g) => {
                  const full = seatedOf(selected.id).length >= selected.capacity;
                  return (
                    <button key={g.id} disabled={full} onClick={() => assign(g.id, selected.id)} data-testid={`panel-add-guest-${g.id}`}
                      className="w-full flex items-center gap-2 text-left text-sm text-stone-700 bg-[#FDFBF7] rounded-md px-3 py-2 hover:bg-[#8C9A86]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dotColor(g) }} title={g.rsvp_status || "pending"} />
                      <Plus size={13} className="text-[#8C9A86]" /> {g.first_name} {g.last_name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      </Gate>
    </EventLayout>
  );
}
